import { deleteApi, postApi } from "../services/api.ts";
import { setContent, setError, setSmallLoading } from "../services/dom.ts";
import { getStringFromLocalStorage } from "../services/localStorage.ts";
import { PlanDb } from "../services/indexeddb/planDb.ts";
import { RecipeDb } from "../services/indexeddb/recipeDb.ts";
import mealPlanItemTemplate from "../templates/components/mealplanitem.html?raw";
import { EMeal, IMealPlan } from "../types/plan.ts";
import { IRecipe } from "../types/recipe.ts";
import { IUser } from "../types/user.ts";
import Message from "./message.ts";

const apiUrl = import.meta.env.VITE_API_URL as string;
const apiToken = getStringFromLocalStorage("idToken");

const peopleOptions = [1, 2, 3, 4];

export default class MealPlanItem extends HTMLElement {
  private planDb = new PlanDb();
  private recipeDb = new RecipeDb();
  private recipeSelect: HTMLSelectElement;
  private recipePeopleSelect: HTMLSelectElement;
  // private applyAllButton: HTMLButtonElement;
  private toggleLeftoversButton: HTMLButtonElement;
  private mealPlans!: IMealPlan[];
  private mealPlan!: IMealPlan;
  public message: Message;
  constructor(user: IUser, date: string, meal: string) {
    super();

    // Populate the Element
    this.innerHTML = mealPlanItemTemplate;

    // Instantiate the Message
    this.message = new Message();

    // Set the Meal Heading
    this.querySelector("[data-selector=meal]")!.innerHTML = meal;

    // Get the Elements
    this.recipeSelect = this.querySelector("[data-selector=recipeSelect]") as HTMLSelectElement;
    this.recipePeopleSelect = this.querySelector("[data-selector=recipePeople]") as HTMLSelectElement;
    // this.applyAllButton = this.querySelector("[data-selector=applyAll]") as HTMLButtonElement;
    this.toggleLeftoversButton = this.querySelector("[data-selector=toggleLeftovers]") as HTMLButtonElement;

    this.loadItem(user, date, meal);
  }

  private async loadItem(user: IUser, date: string, meal: string): Promise<void> {
    // Get the MealPlan if it exists, create an empty one otherwise. And populate relevant fields.
    this.mealPlans = await this.planDb.getPlans();
    this.mealPlan = this.mealPlans.find((searchMealPlan) => searchMealPlan.date === date && searchMealPlan.meal === meal && searchMealPlan.user.email === user.email) as IMealPlan;
    if (this.mealPlan) {
      this.recipeSelect.options.add(new Option(this.mealPlan.recipe.name, this.mealPlan.recipe.id.toString()));
      this.recipePeopleSelect.options.add(new Option(`x${this.mealPlan.number_people.toString()}`, this.mealPlan.number_people.toString()));
      if (this.mealPlan.is_leftovers) {
        this.setActive(this.toggleLeftoversButton.parentElement!);
      }
    } else {
      this.mealPlan = {
        user: user,
        meal: meal as EMeal,
        date: date,
        recipe: {
          name: "--",
          id: 0,
          default_people: 1,
        },
        number_people: 1,
        is_leftovers: false,
      };
      this.recipePeopleSelect.options.add(new Option("x1", "1"));
    }

    this.formatItem();

    // Populate remaining options feilds
    this.recipeSelect.options.add(new Option("--", "--"));
    const recipes = (await this.recipeDb.getRecipes()).sort((a, b) => a.name.localeCompare(b.name));
    recipes.forEach((recipe: IRecipe) => {
      this.recipeSelect.options.add(new Option(recipe.name, recipe.id.toString()));
    });
    peopleOptions.forEach((peopleOption) => {
      if (peopleOption != this.mealPlan.number_people) {
        this.recipePeopleSelect.options.add(new Option(`x${peopleOption.toString()}`, peopleOption.toString()));
      }
    });

    this.formatItem();

    // Add Event Listeners
    this.recipeSelect.addEventListener("change", (event: Event) => {
      this.selectRecipeForMeal(event);
    });
    this.recipePeopleSelect.addEventListener("change", (event: Event) => {
      this.selectMealMultiplier(event);
    });
    // this.applyAllButton.addEventListener("click", () => {
    //   this.applyAll();
    // });
    this.toggleLeftoversButton.addEventListener("click", () => {
      this.toggleLeftovers();
    })
  }

  private formatItem(): void {
    if (this.mealPlan.recipe.name && this.mealPlan.recipe.name != "--") {
      this.setActive(this.recipeSelect);
    } else {
      this.setInactive(this.recipeSelect);
    }

    if (this.mealPlan.is_leftovers) {
      this.setActive(this.toggleLeftoversButton.parentElement!);
    } else {
      this.setInactive(this.toggleLeftoversButton.parentElement!);
    }
  }

  private async selectRecipeForMeal(event: Event): Promise<void> {
    setSmallLoading(this.recipeSelect);
    try {
      const target = event.target as HTMLSelectElement;
      const recipeName = target.selectedOptions[0].textContent as string;
      const newMealPlan = { ...this.mealPlan, recipe: { name: recipeName, id: 0, default_people: this.mealPlan.recipe.default_people } };
      await this.upsertMealPlan(newMealPlan);
      this.replaceMealPlan(newMealPlan);
      setContent(this.recipeSelect);
      this.formatItem();
    } catch (e: any) {
      console.log(e);
      this.message.timedMessage(`⚠ ${e} ⚠`);
      setError(this.recipeSelect);
      console.log(this.mealPlan.recipe.name);
      this.recipeSelect.value = this.mealPlan.recipe.id.toString();
      throw new Error(e);
    }
  }

  private async selectMealMultiplier(event: Event): Promise<void> {
    setSmallLoading(this.recipePeopleSelect);
    try {
      const target = event.target as HTMLSelectElement;
      const reciplePeople = Number(target.selectedOptions[0].value);
      const newMealPlan = { ...this.mealPlan, number_people: reciplePeople };
      await this.upsertMealPlan(newMealPlan);
      this.replaceMealPlan(newMealPlan);
      setContent(this.recipePeopleSelect);
    } catch (e: any) {
      console.log(e);
      this.message.timedMessage(`⚠ ${e} ⚠`);
      setError(this.recipePeopleSelect);
      this.recipePeopleSelect.value = this.mealPlan.number_people.toString();
      throw new Error(e);
    }
  }

  private async toggleLeftovers(): Promise<void> {
    try {
      const newMealPlan = { ...this.mealPlan, is_leftovers: !this.mealPlan.is_leftovers };
      await this.upsertMealPlan(newMealPlan);
      this.mealPlan = newMealPlan;
      this.formatItem();
    } catch (e: any) {
      console.log(e);
      this.message.timedMessage(`⚠ ${e} ⚠`);
      setError(this.toggleLeftoversButton);
      this.toggleLeftoversButton.value = this.mealPlan.is_leftovers.toString();
    }
  }

  // private async applyAll(): Promise<void> {}

  private async upsertMealPlan(mealPlan: IMealPlan): Promise<void> {
    const data: { [key: string]: string | number } = {};
    data.user_email = mealPlan.user.email;
    data.date = mealPlan.date;
    data.meal = mealPlan.meal;

    try {
      if (mealPlan.recipe.name != "--") {
        data.recipe_name = mealPlan.recipe.name;
        data.number_people = mealPlan.number_people;

        data.is_leftovers = mealPlan.is_leftovers.toString();
        await postApi(apiUrl, "mealplan", data, apiToken);
      } else {
        await deleteApi(apiUrl, "mealplan", undefined, apiToken, data);
      }
    } catch {
      throw new Error("Error setting MealPlan");
    }
  }

  private setActive(element: HTMLElement): void {
    element.classList.add("bg-gray-500");
    element.classList.remove("bg-gray-700");
  }

  private setInactive(element: HTMLElement): void {
    element.classList.add("bg-gray-700");
    element.classList.remove("bg-gray-500");
  }

  private replaceMealPlan(plan: IMealPlan): void {
    this.mealPlan = plan;
    this.planDb.updatePlan(plan);
  }
}

customElements.define("meal-plan-item", MealPlanItem);
